import { Box } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

interface TermsContentScrollProps {
  html: string;
  testId: string;
}

/**
 * Terms HTML pane with a custom scrollbar that stays visible on macOS,
 * where native overlay scrollbars are otherwise hidden.
 */
const TermsContentScroll = ({ html, testId }: TermsContentScrollProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [thumb, setThumb] = useState({ top: 0, height: 24 });

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (clientHeight <= 0) {
      return;
    }
    if (scrollHeight <= clientHeight) {
      setThumb({ top: 0, height: clientHeight });
      return;
    }
    const height = Math.max((clientHeight / scrollHeight) * clientHeight, 24);
    const maxTop = clientHeight - height;
    const top = (scrollTop / (scrollHeight - clientHeight)) * maxTop;
    setThumb({ top, height });
  }, []);

  useEffect(() => {
    updateThumb();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => updateThumb());
    observer.observe(el);
    return () => observer.disconnect();
  }, [html, updateThumb]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        alignItems: "stretch",
      }}
    >
      <Box
        ref={scrollRef}
        data-testid={testId}
        onScroll={updateThumb}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "scroll",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& p": { mb: 1.5 },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <Box
        aria-hidden
        data-testid={`${testId}-scrollbar`}
        sx={{
          width: 10,
          flexShrink: 0,
          ml: 0.75,
          bgcolor: "grey.200",
          borderRadius: 1,
          position: "relative",
        }}
      >
        <Box
          data-testid={`${testId}-scrollbar-thumb`}
          sx={{
            position: "absolute",
            left: 1,
            right: 1,
            top: thumb.top,
            height: thumb.height,
            bgcolor: "grey.500",
            borderRadius: 1,
          }}
        />
      </Box>
    </Box>
  );
};

export default TermsContentScroll;
